<script lang="ts">
/**
 * @deprecated This component is still under active development and its API might change in patch
 *   releases.
 * @experimental
 */
export default {};
</script>

<script lang="ts" setup>
import { computed, useId, type HTMLAttributes } from "vue";
import { useDensity } from "../../composables/density.js";
import { useErrorClass } from "../../composables/useErrorClass.js";
import {
  SKELETON_INJECTED_SYMBOL,
  useSkeletonContext,
} from "../../composables/useSkeletonState.js";
import { useVModel } from "../../composables/useVModel.js";
import { mergeVueProps } from "../../utils/attrs.js";
import { useForwardProps } from "../../utils/props.js";
import { FORM_INJECTED_SYMBOL, useFormContext } from "../OnyxForm/OnyxForm.core.js";
import OnyxLoadingIndicator from "../OnyxLoadingIndicator/OnyxLoadingIndicator.vue";
import OnyxSkeleton from "../OnyxSkeleton/OnyxSkeleton.vue";
import MaybePopoverLayout from "./MaybePopoverLayout.vue";
import OnyxFormElementV2Bottom from "./OnyxFormElementV2Bottom.vue";
import OnyxFormElementV2Label from "./OnyxFormElementV2Label.vue";
import type {
  FormElementV2LabelOptions,
  OnyxFormElementV2Props,
  OnyxFormElementV2Slots,
} from "./types.js";

const props = withDefaults(
  defineProps<
    OnyxFormElementV2Props & {
      /**
       * *Wether it's unstyled, in which case it resets the styles of the content area (where the
       * main form element resides)
       */
      unstyled?: boolean;
    }
  >(),
  {
    skeleton: SKELETON_INJECTED_SYMBOL,
    requiredMarker: FORM_INJECTED_SYMBOL,
    showError: FORM_INJECTED_SYMBOL,
    reserveMessageSpace: FORM_INJECTED_SYMBOL,
    open: undefined,
    popoverOptions: () => ({ fitParent: true }),
    id: () => useId(),
  },
);

const emit = defineEmits<{
  /**
   * Emitted when the popover open state changes.
   */
  "update:open": [open: boolean];
}>();

const slots = defineSlots<OnyxFormElementV2Slots>();

const { densityClass } = useDensity(props);
const formContext = useFormContext(props);
const errorClass = useErrorClass(formContext.showError);
const skeleton = useSkeletonContext(props);
const open = useVModel({ props, emit, key: "open", default: false });

const label = computed<FormElementV2LabelOptions>(() => {
  if (typeof props.label === "object") return props.label;
  return { label: props.label };
});

const inputProps = computed<HTMLAttributes>(() => {
  return {
    id: props.id,
    class: ["onyx-form-element-v2__input", "onyx-truncation-ellipsis"],
    required: props.required,
    disabled: props.loading,
    ...(label.value.hidden
      ? {
          title: label.value.label,
          "aria-label": label.value.label,
        }
      : {}),
    // prevent showing virtual keyboards on e.g. smartphones
    // since the input is not editable if a popover exists
    // see: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/inputmode
    ...(slots.popover ? { inputmode: "none" } : {}),
  };
});

const topProps = useForwardProps(props, OnyxFormElementV2Label);
const bottomProps = useForwardProps(props, OnyxFormElementV2Bottom);
const popoverLayoutProps = useForwardProps(props, MaybePopoverLayout);
</script>

<template>
  <div
    :class="[
      'onyx-component',
      'onyx-form-element-v2',
      densityClass,
      errorClass,
      {
        [`onyx-form-element-v2--label-${label.position}`]: label.position !== 'top',
        'onyx-form-element-v2--unstyled': props.unstyled,
      },
    ]"
  >
    <OnyxFormElementV2Label
      v-if="!label.hidden && !(skeleton && label.position === 'left')"
      v-bind="topProps"
    />

    <div class="onyx-form-element-v2__body">
      <OnyxSkeleton v-if="skeleton" class="onyx-form-element-v2__content-skeleton" />

      <div v-else class="onyx-form-element-v2__content">
        <div
          v-if="slots.leading"
          class="onyx-form-element-v2__slot onyx-form-element-v2__slot--leading"
        >
          <slot name="leading"></slot>
        </div>

        <MaybePopoverLayout v-bind="popoverLayoutProps" v-model:open="open">
          <template #default="{ input: popoverInputProps }">
            <div class="onyx-form-element-v2__input-container">
              <div
                v-if="slots.leadingIcons || props.loading"
                class="onyx-form-element-v2__icons onyx-form-element-v2__icons--leading"
              >
                <OnyxLoadingIndicator
                  v-if="props.loading"
                  class="onyx-form-element-v2__loading"
                  type="circle"
                />
                <slot v-else name="leadingIcons"></slot>
              </div>

              <slot v-bind="mergeVueProps(inputProps, popoverInputProps)"></slot>

              <div
                v-if="slots.trailingIcons"
                class="onyx-form-element-v2__icons onyx-form-element-v2__icons--trailing"
              >
                <slot name="trailingIcons"></slot>
              </div>
            </div>
          </template>

          <template v-if="slots.popover" #popover>
            <slot name="popover"></slot>
          </template>
        </MaybePopoverLayout>

        <div
          v-if="slots.trailing"
          class="onyx-form-element-v2__slot onyx-form-element-v2__slot--trailing"
        >
          <slot name="trailing"></slot>
        </div>
      </div>

      <OnyxFormElementV2Bottom v-bind="bottomProps">
        <template v-if="slots.bottomRight" #bottomRight>
          <slot name="bottomRight"></slot>
        </template>
      </OnyxFormElementV2Bottom>
    </div>
  </div>
</template>

<style lang="scss">
@use "../../styles/mixins/layers.scss";
@use "../../styles/mixins/density.scss";
@use "../../styles/mixins/text.scss";
@use "./OnyxFormElementV2.scss";

.onyx-form-element-v2 {
  @include layers.component() {
    --onyx-form-element-v2-gap: var(--onyx-density-3xs);
    --onyx-form-element-v2-border-radius: var(--onyx-radius-component-input);
    --onyx-form-element-v2-border-size: var(--onyx-1px-in-rem);
    --onyx-form-element-v2-border-color: var(--onyx-color-component-border-secondary);
    --onyx-form-element-v2-border-color-hover: var(--onyx-color-component-border-secondary-hover);
    --onyx-form-element-v2-border-color-focus: var(--onyx-color-component-border-primary);
    --onyx-form-element-v2-background: var(--onyx-color-base-background-blank);
    --onyx-form-element-v2-background-hover: var(--onyx-form-element-v2-background);
    --onyx-form-element-v2-background-autofill: var(--onyx-color-base-warning-100);
    --onyx-form-element-v2-padding-block: var(--onyx-density-xs);
    --onyx-form-element-v2-padding-inline: var(--onyx-density-sm);
    --onyx-form-element-v2-padding-inline-icons: var(--onyx-density-xs);
    --onyx-form-element-v2-caret-color: var(--onyx-color-component-cta-default);
    --onyx-form-element-v2-selection-background: var(--onyx-color-base-primary-200);
    --onyx-form-element-v2-outline-color: var(--onyx-color-component-focus-primary);
    --onyx-form-element-v2-error-display: none;
    --onyx-form-element-v2-message-display: flex;
    --onyx-form-element-v2-input-width: 100%;

    /** Base content and skeleton height. Useful when e.g. changing the base height for textarea etc. */
    --onyx-form-element-v2-content-height: 1lh;
    --onyx-form-element-v2-label-skeleton-height: 1lh;

    // :read-only is valid for readonly and disabled state so we put shared styles for both states here
    &:has(.onyx-form-element-v2__input:read-only) {
      --onyx-form-element-v2-selection-background: var(--onyx-color-base-neutral-200);
      --onyx-form-element-v2-caret-color: var(--onyx-color-base-neutral-700);
      --onyx-form-element-v2-background: var(--onyx-color-base-background-tinted);
    }

    // styles for readonly but NOT disabled
    &:has(.onyx-form-element-v2__input:enabled:read-only) {
      --onyx-form-element-v2-border-color-hover: var(--onyx-color-component-border-neutral-hover);
      --onyx-form-element-v2-border-color-focus: var(--onyx-color-component-border-neutral);
      --onyx-form-element-v2-outline-color: var(--onyx-color-component-focus-neutral);
    }

    // see "useErrorClass" composable
    &:not(.onyx-form-element--suppress-invalid) {
      &.onyx-form-element--touched-invalid:has(
          .onyx-form-element-v2__input:user-invalid,
          .onyx-form-element-v2__input--touched:invalid,
          .onyx-form-element-v2__input--touched[aria-invalid="true"]
        ),
      &.onyx-form-element--immediate-invalid:has(
          .onyx-form-element-v2__input:invalid,
          .onyx-form-element-v2__input[aria-invalid="true"]
        ) {
        --onyx-form-element-v2-border-color: var(--onyx-color-component-border-danger);
        --onyx-form-element-v2-border-color-hover: var(--onyx-color-component-border-danger-hover);
        --onyx-form-element-v2-border-color-focus: var(--onyx-color-component-border-danger);
        --onyx-form-element-v2-outline-color: var(--onyx-color-component-focus-danger);
        --onyx-form-element-v2-selection-background: var(--onyx-color-base-danger-200);
        --onyx-form-element-v2-caret-color: var(--onyx-color-base-neutral-900);
        --onyx-form-element-v2-error-display: flex;
        --onyx-form-element-v2-message-display: none;
      }
    }

    &:has(.onyx-form-element-v2__message--success) {
      --onyx-form-element-v2-border-color: var(--onyx-color-component-border-success);
      --onyx-form-element-v2-border-color-hover: var(--onyx-color-component-border-success-hover);
      --onyx-form-element-v2-border-color-focus: var(--onyx-color-component-border-success);
      --onyx-form-element-v2-outline-color: var(--onyx-color-component-focus-success);
      --onyx-form-element-v2-selection-background: var(--onyx-color-base-success-200);
      --onyx-form-element-v2-caret-color: var(--onyx-color-base-neutral-900);
    }
  }

  // the skeleton gap would be 0 in compact density so we shrink the label size a bit and increase the gap so it does not look off
  @include density.compact {
    &:has(.onyx-form-element-v2__content-skeleton) {
      --onyx-form-element-v2-gap: var(--onyx-spacing-5xs);
      --onyx-form-element-v2-label-skeleton-height: calc(1lh - var(--onyx-form-element-v2-gap));
    }
  }
}

.onyx-form-element-v2 {
  @include layers.component() {
    font-family: var(--onyx-font-family-paragraph);
    display: flex;
    flex-direction: column;
    gap: var(--onyx-form-element-v2-gap);
    color: var(--onyx-color-text-icons-neutral-intense);
    font-size: var(--onyx-font-size-md);
    line-height: var(--onyx-font-line-height-md);
    max-width: 100%;

    &--label-left,
    &--label-right {
      --onyx-form-element-v2-gap: var(--onyx-density-lg);
      align-items: flex-start;
      justify-content: space-between;

      > .onyx-form-element-v2__label {
        width: max-content;
        padding-block: calc(var(--onyx-form-element-v2-padding-block));
        line-height: var(--onyx-font-line-height-md);
      }
    }

    &--label-left {
      flex-direction: row;
    }

    &--label-right {
      flex-direction: row-reverse;
    }

    &__body {
      display: flex;
      flex-direction: column;
      gap: var(--onyx-density-3xs);
      width: 100%;
    }

    &__content {
      border-radius: var(--onyx-form-element-v2-border-radius);
      background-color: var(--onyx-form-element-v2-background);
      display: flex;
      align-items: center;
      height: calc(
        var(--onyx-form-element-v2-content-height) + 2 * var(--onyx-form-element-v2-padding-block)
      );
    }

    &__input-container {
      display: flex;
      align-items: center;
      flex-grow: 1;
      border: var(--onyx-form-element-v2-border-size) solid var(--onyx-form-element-v2-border-color);
      border-radius: inherit;
      height: 100%;
      max-width: 100%;

      &:has(.onyx-form-element-v2__input:read-write):hover {
        border-color: var(--onyx-form-element-v2-border-color-hover);
      }

      &:has(.onyx-form-element-v2__input:autofill),
      // used for Playwright screenshot tests
      &:has(.onyx-form-element-v2__input[data-test-autofill]) {
        background-color: var(--onyx-form-element-v2-background-autofill);
      }

      &:has(.onyx-form-element-v2__icons--leading) .onyx-form-element-v2__input {
        padding-left: var(--onyx-form-element-v2-padding-inline-icons);
      }

      &:has(.onyx-form-element-v2__icons--trailing) .onyx-form-element-v2__input {
        padding-right: var(--onyx-form-element-v2-padding-inline-icons);
      }
    }

    @include OnyxFormElementV2.input-focus-or-popover-open() {
      border-color: var(--onyx-form-element-v2-border-color-focus);
      outline: var(--onyx-outline-width) solid var(--onyx-form-element-v2-outline-color);
      background-color: var(--onyx-form-element-v2-background-hover);
    }

    @include OnyxFormElementV2.input-container-hover() {
      background-color: var(--onyx-form-element-v2-background-hover);
    }

    &__icons {
      color: var(--onyx-color-text-icons-neutral-medium);
      display: flex;
      align-items: center;
      padding: var(--onyx-form-element-v2-padding-block) var(--onyx-form-element-v2-padding-inline);
      gap: var(--onyx-density-2xs);

      &--leading {
        padding-right: 0;
      }

      &--trailing {
        padding-left: 0;
      }
    }

    &__slot {
      height: 100%;
      border: var(--onyx-form-element-v2-border-size) solid var(--onyx-form-element-v2-border-color);
      display: flex;
      align-items: center;

      &--leading {
        border-right: none;
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;
      }

      &--trailing {
        border-left: none;
        border-top-right-radius: inherit;
        border-bottom-right-radius: inherit;
      }

      // override nested styles in slots to seamlessly integrate e.g. OnyxSelect
      .onyx-form-element-v2 {
        --onyx-form-element-v2-input-width: #{text.ch(3)};
        --onyx-form-element-v2-border-size: 0;
        --onyx-form-element-v2-background: transparent;
      }
    }

    // if the form element has no input focus or no open popover, the border radius is set to 0 if it has leading/trailing slot
    &__content {
      &:has(> .onyx-form-element-v2__slot--leading) {
        > .onyx-form-element-v2__popover,
        > .onyx-form-element-v2__input-container {
          &:not(:has(.onyx-form-element-v2__input:focus)):not(
              :has(.onyx-basic-popover__dialog:popover-open)
            ) {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
        }
      }

      &:has(> .onyx-form-element-v2__slot--trailing) {
        > .onyx-form-element-v2__popover,
        > .onyx-form-element-v2__input-container {
          &:not(:has(.onyx-form-element-v2__input:focus)):not(
              :has(.onyx-basic-popover__dialog:popover-open)
            ) {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }
        }
      }
    }

    &__input {
      border: none;
      border-radius: inherit;
      background-color: transparent;
      color: inherit;
      width: calc(
        var(--onyx-form-element-v2-input-width) + 2 * var(--onyx-form-element-v2-padding-inline)
      );
      max-width: 100%;
      height: 100%;
      outline: none;
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      caret-color: var(--onyx-form-element-v2-caret-color);
      padding: var(--onyx-form-element-v2-padding-block) var(--onyx-form-element-v2-padding-inline);
      cursor: inherit;

      &::placeholder {
        color: var(--onyx-color-text-icons-neutral-soft);
        font-weight: var(--onyx-font-weight-regular);
        opacity: 1;
      }

      &::selection {
        background: var(--onyx-form-element-v2-selection-background);
      }

      // hide default browser icons
      &::-webkit-search-cancel-button,
      &::-webkit-calendar-picker-indicator {
        display: none;
      }

      // remove stepper arrows
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      &[type="number"] {
        -moz-appearance: textfield;
      }

      &:autofill {
        background-color: transparent;
        -webkit-text-fill-color: var(--onyx-color-text-icons-neutral-intense);

        // many browsers use "!important" to set the autofill background so we need this
        // transition workaround to make the background transparent
        transition: background-color calc(infinity * 1s);
      }
    }

    &__tooltip {
      margin-left: var(--onyx-spacing-2xs);
    }

    // ensure popover does not overlap label or bottom area
    &:has(&__bottom:not(:empty)):has(&__popover > .onyx-basic-popover__dialog--position-bottom),
    &:has(> .onyx-form-element-v2__label):has(
        &__popover > .onyx-basic-popover__dialog--position-top
      ) {
      .onyx-form-element-v2__popover {
        --onyx-basic-popover-gap: var(--onyx-form-element-v2-bottom-height);
      }
    }

    &__loading {
      color: var(--onyx-color-text-icons-primary-intense);
    }

    // skeletons
    &__content-skeleton {
      width: 100%;
      height: calc(
        var(--onyx-form-element-v2-content-height) + 2 * var(--onyx-form-element-v2-padding-block)
      );
    }
  }
  &--unstyled {
    > .onyx-form-element-v2__body {
      > .onyx-form-element-v2__content {
        background-color: transparent;
        > .onyx-form-element-v2__input-container {
          border: none;
          outline: none;
          &:focus-within,
          &:hover {
            background-color: transparent;
          }
        }
      }
    }
  }
}
</style>
